/**
 * NotificacionesView -- LA BANDEJA DEL PROPIETARIO.
 *
 * MISION/TAREA: [PANEL-NOTIF-T1].
 *
 * AUDIENCIA: EL PROPIETARIO del negocio, en el panel, escritorio. Es quien
 * DECIDE sobre cosas que otro pidio o que el sistema detecto. La tablet del
 * colaborador NO es esta pantalla: alli se trabaja, aqui se resuelve.
 *
 * LAS DOS MITADES:
 *
 *  (a) SOLICITUDES QUE LA CAJA ESCALA (anular o cambiar un egreso, una venta,
 *      una factura). Hasta el 15-sep esta mitad pintaba un hueco con su causa:
 *      no habia puente. Ese dia el API ya servia la bandeja (401 en las dos
 *      rutas, 404 en sus pares inventados), la version 1226 del Desktop subia
 *      las solicitudes y habia 7 reales de Exito Chino. Se lee y se decide con
 *      SolicitudesAprobacionApiClient; lo que se muestra y como se arma el
 *      veredicto vive en application/aprobaciones/solicitudes.js.
 *
 *      LO QUE ESTA PANTALLA NO ESCONDE: esas 7 son del 3 al 30 de agosto. Cada
 *      solicitud dice cuantos dias tiene, las viejas lo avisan antes de los
 *      botones, y dos pedidos sobre el mismo movimiento se senalan entre si.
 *
 *  (b) INSUMOS EN CERO O NEGATIVO. Medido en EC: 57 insumos, 7 en cero.
 */

import { InventarioApiClient } from '../../infrastructure/api/InventarioApiClient.js';
import { SolicitudesAprobacionApiClient } from '../../infrastructure/api/SolicitudesAprobacionApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { AppConfig } from '../../config.js';
import {
  leerListado, diasDesde, antiguedadEnPalabras, estadoEnPalabras, quienDecide,
  leerRespuestaDelVeredicto, DIAS_PARA_AVISAR, DECIDIR_DESDE_PANEL, RAZON_SIN_DECIDIR, queCambia, queHaceLaCaja, laMasVieja, gruposParaDecidir
} from '../../application/aprobaciones/solicitudes.js';

const C = {
  alerta: 'var(--accent-red)', ojo: 'var(--accent-yellow)', bien: 'var(--accent-green-text)', frio: 'var(--accent-blue)',
  texto: 'var(--text-primary)', suave: 'var(--text-secondary)', tenue: 'var(--text-muted)',
  panel: 'var(--bg-secondary)', fondo: 'var(--bg-canvas)', borde: 'var(--border-default)'
};

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearNumero } from '../shared/numeroColombiano.js';
const nfmt = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : formatearNumero(Number(n)));

/**
 * Lee los insumos y separa los que estan en cero o en negativo.
 * Tolera las dos formas de la clave (Inventario del DTO, inventario de BD):
 * al LEER se perdona, cuesta una linea y protege.
 */
function insumosEnCero(items) {
  const val = (i) => {
    const v = i.Inventario !== undefined ? i.Inventario
      : (i.inventario !== undefined ? i.inventario : null);
    return v === null || v === '' ? null : Number(v);
  };
  return (items || [])
    .map((i) => ({
      nombre: i.nombre || i.Nombre || '(sin nombre)',
      grupo: i.Grupo || i.grupo || '',
      cantidad: val(i)
    }))
    .filter((i) => i.cantidad !== null && !Number.isNaN(i.cantidad) && i.cantidad <= 0)
    .sort((a, b) => a.cantidad - b.cantidad);
}

/** Quien decide, desde el JWT del panel. El API re-valida el token. */
function payloadDelToken() {
  try {
    const t = AppConfig.token;
    if (!t) return null;
    const b64 = t.split('.')[1].split('-').join('+').split('_').join('/');
    return JSON.parse(atob(b64));
  } catch (e) {
    return null;
  }
}

function nombreDelNegocio() {
  // CONTRATO-VISTA-PANEL-T1: el nombre por la pieza del negocio elegido, no leyendo el navegador a mano.
  try { return getSelectedBusinessName() || 'este negocio'; } catch (e) { return 'este negocio'; }
}

// --- (a) las solicitudes -------------------------------------------------------

const MOTIVOS_APROBACIONES = {
  SIN_NEGOCIO: ['Elegi un negocio', 'Arriba, en el selector, para ver lo que su caja te pidio aprobar.'],
  FORBIDDEN: ['No tenes permiso para ver estas solicitudes', 'Las ve el propietario del negocio.'],
  NETWORK: ['No hubo conexion con el servidor', 'No pudimos preguntar por las solicitudes. Reintenta en un momento.'],
  ERROR: ['No pudimos leer las solicitudes', 'El servidor no respondio bien. Reintenta en un momento.']
};

function tarjetaSolicitud(s) {
  const dias = diasDesde(s.fechaOperativa);
  const vieja = dias !== null && dias > DIAS_PARA_AVISAR;
  // FRONT-VISTAS-EC-V04-T1: todo a 14px (antes 11-13); un solo aviso por tarjeta: si el dia ya esta cuadrado, ese
  // manda (la caja no aplica nada) y el de "vieja" sobra; si no, el de vieja.
  return '<div data-solicitud="' + esc(s.syncId) + '" style="padding:12px;border-top:1px solid ' + C.borde + ';font-size:14px;line-height:1.5">'
    + '<div style="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap">'
    + '<span style="color:' + C.texto + ';font-weight:700">' + esc(s.etiqueta) + '</span>'
    + '<span data-antiguedad style="margin-left:auto;color:' + (vieja ? C.ojo : C.suave) + ';white-space:nowrap">'
    + esc(s.fechaOperativa || '') + ' · ' + esc(antiguedadEnPalabras(dias)) + '</span></div>'
    + '<div style="color:' + C.texto + ';margin-top:4px">' + esc(s.resumen || '(sin detalle)') + '</div>'
    + '<div style="color:' + C.suave + ';margin-top:4px">'
    + (s.motivo ? 'Motivo: &ldquo;' + esc(s.motivo) + '&rdquo; · ' : '')
    + 'pidio ' + esc(s.solicitante || 'alguien en la caja') + '</div>'
    + (s.diaArqueado === true
      ? '<div data-aviso-arqueado style="color:' + C.ojo + ';margin-top:8px">'
        + '&#9888;&#65039; ' + (s.fechaRevisada ? 'El ' + esc(String(s.fechaRevisada).slice(0, 10)) : 'Ese día') + ' ya tiene arqueo: la caja no aplica cambios sobre un día cuadrado.</div>'
      : vieja
        ? '<div data-aviso-vieja style="color:' + C.ojo + ';margin-top:8px">'
          + '&#9888;&#65039; Se pidio en la caja el ' + esc(s.fechaOperativa) + ' y llego al panel despues: es ' + esc(antiguedadEnPalabras(dias))
          + '. Esa caja puede estar ya cuadrada.</div>'
        : '')
    + (s.otrasSobreLoMismo > 0
      ? '<div data-aviso-misma-referencia style="color:' + C.ojo + ';margin-top:6px">'
        + 'Hay ' + s.otrasSobreLoMismo + ' solicitud' + (s.otrasSobreLoMismo > 1 ? 'es' : '') + ' mas sobre este mismo movimiento. Miralas juntas antes de decidir.</div>'
      : '')
    + (DECIDIR_DESDE_PANEL ? botonesDeDecision(s) : '')
    + '</div>';
}

/** Solo se pintan si DECIDIR_DESDE_PANEL: ver la razon en solicitudes.js. La confirmacion dice que cambia y deja una nota. */
function botonesDeDecision(s) {
  return '<div data-que-cambia hidden data-tipo="' + esc(s.tipo) + '" data-arqueado="' + (s.diaArqueado === true ? '1' : '') + '" data-que-hace="' + esc(queHaceLaCaja(s.tipo, s.diaArqueado)) + '">' + esc(queCambia(s)) + '</div>'
    + '<div data-acciones style="display:flex;gap:8px;margin-top:10px">'
    + '<button type="button" class="btn-primary btn-sm" data-decidir="aprobada" style="font-size:14px">Aprobar</button>'
    + '<button type="button" class="btn-ghost btn-sm" data-decidir="rechazada" style="font-size:14px">Rechazar</button></div>'
    + '<div data-confirmar hidden style="margin-top:10px;font-size:14px;color:' + C.texto + '">'
    + '<div data-confirmar-texto style="margin-bottom:8px;line-height:1.5"></div>'
    + '<textarea data-nota maxlength="500" rows="2" placeholder="Nota para quien lo pidio (opcional)" style="width:100%;box-sizing:border-box;margin-bottom:8px;font:inherit;font-size:14px"></textarea>'
    + '<div style="display:flex;gap:8px">'
    + '<button type="button" class="btn-primary btn-sm" data-confirmar-si style="font-size:14px"></button>'
    + '<button type="button" class="btn-ghost btn-sm" data-confirmar-no style="font-size:14px">Cancelar</button></div></div>'
    + '<div data-resultado style="font-size:14px;margin-top:8px"></div>';
}

function bloqueAprobaciones(listado, motivo) {
  const marco = (borde, cabecera, cuerpo) => '<div data-bandeja-aprobaciones style="background:' + C.panel
    + ';border:1px solid ' + C.borde + ';border-left:3px solid ' + borde + ';border-radius:10px;overflow:hidden">'
    + '<div style="padding:14px 12px 10px"><div style="display:flex;align-items:center;gap:8px">'
    + '<span style="font-size:1.1rem">&#9878;&#65039;</span>'
    + '<span style="font-size:14px;color:' + C.texto + ';font-weight:700">Cosas para aprobar</span>'
    + cabecera + '</div></div>' + cuerpo + '</div>';

  if (motivo) {
    const m = MOTIVOS_APROBACIONES[motivo] || MOTIVOS_APROBACIONES.ERROR;
    const dev = AppConfig.env === 'dev'
      ? '<div style="font-size:14px;color:' + C.tenue + ';margin-top:6px">Estas en dev: se pregunto a ' + esc(AppConfig.apiBase) + '. En produccion la bandeja existe.</div>'
      : '';
    return marco(C.tenue, '', '<div style="padding:0 12px 14px"><div style="font-size:0.875rem;color:' + C.texto + ';font-weight:600">'
      + esc(m[0]) + '</div><div style="font-size:14px;color:' + C.suave + ';margin-top:6px">' + esc(m[1]) + '</div>' + dev + '</div>');
  }

  const n = listado.porDecidir.length;
  const cabecera = '<span data-bandeja-pendientes style="margin-left:auto;font-size:14px;color:' + (n ? C.ojo : C.bien)
    + ';font-weight:700">' + n + '</span>';
  const notas = [];
  if (listado.decididas.length) notas.push(listado.decididas.length + ' ya decidida' + (listado.decididas.length > 1 ? 's' : '') + ' (no se muestran)');
  if (listado.pendientesSegunApi !== null && listado.pendientesSegunApi !== n) {
    notas.push('el servidor cuenta ' + listado.pendientesSegunApi + ' por decidir y llegaron ' + n);
  }
  if (listado.paginaIncompleta) notas.push('llegaron ' + listado.solicitudes.length + ' de ' + listado.total);
  const pie = notas.length
    ? '<div style="padding:10px 12px;font-size:14px;color:' + C.tenue + ';border-top:1px solid ' + C.borde + '">' + esc(notas.join(' · ')) + '</div>'
    : '';

  if (!n) {
    return marco(C.bien, cabecera, '<div style="padding:0 12px 14px;font-size:14px;color:' + C.suave + '">'
      + 'La caja de ' + esc(nombreDelNegocio()) + ' no tiene nada esperando tu decision.</div>' + pie);
  }
  // FRONT-VISTAS-EC-V04-T1: la introduccion repetia lo que ya dicen los grupos; queda solo si no se decide desde aqui.
  const intro = DECIDIR_DESDE_PANEL ? '' : '<div style="padding:0 12px 10px;font-size:14px;color:' + C.suave + ';line-height:1.6">'
    + 'Lo que pidieron en la caja y la caja no deja hacer sin vos. '
    + '<span data-razon-sin-decidir style="color:' + C.texto + '">' + esc(RAZON_SIN_DECIDIR) + '</span>'
    + '</div>';
  // FRONT-VISTAS-EC-V04-T1: por grupos, segun lo que pasaria al aprobar; cada grupo de la mas vieja a la mas nueva.
  const g = gruposParaDecidir(listado.porDecidir);
  const grupo = (clave, titulo, ayuda, lista) => lista.length
    ? '<div data-grupo-aprob="' + clave + '"><div style="padding:12px 12px 4px;font-size:14px;color:' + C.texto + ';font-weight:700;border-top:1px solid ' + C.borde + '">'
      + esc(titulo) + ' (' + lista.length + ')</div><div style="padding:0 12px 6px;font-size:14px;color:' + C.suave + '">' + esc(ayuda) + '</div>'
      + lista.map(tarjetaSolicitud).join('') + '</div>'
    : '';
  const vieja = laMasVieja(listado.porDecidir);
  const resumen = '<div data-aprob-resumen style="padding:0 12px 10px;font-size:14px;color:' + C.texto + '">'
    + n + ' esperan tu decisión' + (vieja ? ' · la más vieja ' + esc(antiguedadEnPalabras(vieja.dias)) : '')
    + (g.sobreDiaCuadrado.length ? ' · ' + g.sobreDiaCuadrado.length + ' caen sobre días ya cuadrados' : '') + '.</div>';
  return marco(C.ojo, cabecera, resumen + intro
    + grupo('aplicables', 'Se aplican al aprobar', 'La caja las aplica la próxima vez que sincronice.', g.aplicables)
    + grupo('sin-saber', 'Sin saber si el día está cuadrado', 'El servidor todavía no dice si ese día tiene arqueo.', g.sinSaber)
    // Plegado: aprobarlas no cambia la caja; se ven al abrir (teclado y toque), sin empujar hacia abajo las que si cuentan.
    + (g.sobreDiaCuadrado.length
      ? '<details data-aprob-plegado style="border-top:1px solid ' + C.borde + '"><summary style="cursor:pointer;padding:12px;font-size:14px;color:' + C.texto + ';font-weight:700">'
        + 'Caen sobre un día ya cuadrado (' + g.sobreDiaCuadrado.length + ')</summary>'
        + '<div style="padding:0 12px 6px;font-size:14px;color:' + C.suave + '">La caja no aplica cambios sobre un día con arqueo: aprobarlas no cambia la caja.</div>'
        + g.sobreDiaCuadrado.map(tarjetaSolicitud).join('') + '</details>'
      : '')
    + pie);
}

/** Los botones de cada tarjeta. Confirmacion en la misma tarjeta, sin confirm() nativo. */
function conectarDecisiones($cuerpo, bizId) {
  $cuerpo.addEventListener('click', async (ev) => {
    const $btn = ev.target.closest('button');
    if (!$btn) return;
    const $t = $btn.closest('[data-solicitud]');
    if (!$t) return;
    const $acc = $t.querySelector('[data-acciones]');
    const $conf = $t.querySelector('[data-confirmar]');
    const $res = $t.querySelector('[data-resultado]');
    const $si = $t.querySelector('[data-confirmar-si]');

    if ($btn.hasAttribute('data-decidir')) {
      const veredicto = $btn.getAttribute('data-decidir');
      $t.dataset.veredicto = veredicto;
      const $que = $t.querySelector('[data-que-cambia]');
      const que = $que.textContent;
      $t.querySelector('[data-confirmar-texto]').textContent = veredicto === 'aprobada'
        ? '¿Aprobar? ' + que + '. ' + $que.getAttribute('data-que-hace') + ' No se puede deshacer desde aca.'
        : '¿Rechazar? ' + que + '. No se aplica nada en la caja.';
      $si.textContent = veredicto === 'aprobada' ? 'Si, aprobar' : 'Si, rechazar';
      $acc.hidden = true;
      $conf.hidden = false;
      $res.textContent = '';
      $si.focus();
      return;
    }
    if ($btn.hasAttribute('data-confirmar-no')) {
      $conf.hidden = true;
      $acc.hidden = false;
      return;
    }
    if ($btn.hasAttribute('data-confirmar-si')) {
      const veredicto = $t.dataset.veredicto;
      const quien = quienDecide(payloadDelToken());
      $conf.querySelectorAll('button').forEach((b) => { b.disabled = true; });
      $si.textContent = 'Guardando...';
      let res;
      try {
        res = await SolicitudesAprobacionApiClient.resolver($t.getAttribute('data-solicitud'), {
          businessId: bizId, veredicto, aprobadorNombre: quien.nombre, aprobadorUuid: quien.uuid,
          nota: $t.querySelector('[data-nota]').value
        });
      } catch (e) {
        res = { ok: false, code: 400, error: e.message };
      }
      const $q = $t.querySelector('[data-que-cambia]');
      const lectura = leerRespuestaDelVeredicto(res, veredicto, $q.getAttribute('data-tipo'), $q.getAttribute('data-arqueado') === '1' ? true : null);
      $res.textContent = lectura.mensaje;
      $res.style.color = lectura.estado === 'decidida' ? C.bien : C.alerta;
      $conf.querySelectorAll('button').forEach((b) => { b.disabled = false; });
      $conf.hidden = true;
      if (lectura.estado === 'decidida') {
        $t.dataset.decidida = veredicto;
        $t.querySelector('[data-antiguedad]').textContent += ' · ' + (veredicto === 'aprobada' ? 'aprobada' : estadoEnPalabras(veredicto));
      } else {
        $acc.hidden = false;
      }
    }
  });
}

// --- (b) insumos -------------------------------------------------------------

function bloqueInsumos(lista, motivo) {
  if (motivo) {
    const M = {
      SIN_NEGOCIO: ['Elegi un negocio', 'Arriba, en el selector, para poder mirar sus insumos.'],
      NO_PUBLICADO: ['No pudimos leer los insumos', 'El listado de insumos no respondio. Se puede reintentar.'],
      ERROR: ['No pudimos leer los insumos', 'Reintenta en un momento.']
    };
    const m = M[motivo] || M.ERROR;
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde + ';border-radius:10px;padding:16px">'
      + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">' + esc(m[0]) + '</div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px">' + esc(m[1]) + '</div></div>';
  }

  if (!lista.length) {
    return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
      + ';border-left:3px solid ' + C.bien + ';border-radius:10px;padding:16px">'
      + '<div style="display:flex;align-items:center;gap:8px">'
      + '<span style="font-size:1.1rem">&#9989;</span>'
      + '<span style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">Ningun insumo en cero</span></div>'
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px">'
      + 'Todo lo que se usa en las recetas tiene existencias. Nada que decidir por aca hoy.</div></div>';
  }

  const filas = lista.map(function (i) {
    const negativo = i.cantidad < 0;
    const color = negativo ? C.alerta : C.ojo;
    return '<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;'
      + 'padding:9px 12px;border-bottom:1px solid ' + C.borde + '">'
      + '<div style="min-width:0"><div style="font-size:0.875rem;color:' + C.texto + ';font-weight:600;'
      + 'white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' + esc(i.nombre) + '</div>'
      + (i.grupo ? '<div style="font-size:0.875rem;color:' + C.tenue + '">' + esc(i.grupo) + '</div>' : '')
      + '</div>'
      + '<div style="font-size:.9rem;color:' + color + ';font-weight:700;font-family:monospace;white-space:nowrap">'
      + nfmt(i.cantidad) + (negativo ? ' &#9888;' : '') + '</div></div>';
  }).join('');

  const hayNegativos = lista.some((i) => i.cantidad < 0);
  const explica = hayNegativos
    ? 'Los que estan en <strong style="color:' + C.alerta + '">negativo</strong> son los urgentes: el sistema cree que se uso mas de lo que habia, asi que en algun lado falta registrar una entrada.'
    : 'Estan en cero: si alguno se usa hoy, la receta no va a poder cobrarse bien.';

  return '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
    + ';border-left:3px solid ' + (hayNegativos ? C.alerta : C.ojo) + ';border-radius:10px;overflow:hidden">'
    + '<div style="padding:14px 12px 10px">'
    + '<div style="display:flex;align-items:center;gap:8px">'
    + '<span style="font-size:1.1rem">&#128230;</span>'
    + '<span style="font-size:0.875rem;color:' + C.texto + ';font-weight:700">Insumos sin existencias</span>'
    + '<span style="margin-left:auto;font-size:0.875rem;color:' + (hayNegativos ? C.alerta : C.ojo)
    + ';font-weight:700">' + lista.length + '</span></div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px;line-height:1.6">' + explica + '</div>'
    + '</div>' + filas
    + '<div style="padding:10px 12px;font-size:0.875rem;color:' + C.tenue + '">'
    + 'Se revisa contra el inventario del negocio en este momento.</div></div>';
}

// --- la vista ---------------------------------------------------------------

async function leerAprobaciones(bizId) {
  if (!bizId) return { listado: null, motivo: 'SIN_NEGOCIO' };
  try {
    const res = await SolicitudesAprobacionApiClient.listar(bizId);
    if (res && res.ok) return { listado: leerListado(res.data), motivo: null };
    const code = res ? res.code : null;
    if (code === 403) return { listado: null, motivo: 'FORBIDDEN' };
    if (code === 'NETWORK' || code === 'TIMEOUT') return { listado: null, motivo: 'NETWORK' };
    return { listado: null, motivo: 'ERROR' };
  } catch (e) {
    return { listado: null, motivo: 'ERROR' };
  }
}

async function leerInsumos(bizId) {
  if (!bizId) return { lista: [], motivo: 'SIN_NEGOCIO' };
  try {
    const res = await InventarioApiClient.getInsumosWithConfig(bizId);
    if (res && res.ok) return { lista: insumosEnCero(res.items || res.data || []), motivo: null };
    return { lista: [], motivo: res && res.fallback ? 'NO_PUBLICADO' : 'ERROR' };
  } catch (e) {
    return { lista: [], motivo: 'ERROR' };
  }
}

export async function renderNotificaciones(container) {
  const $el = container;
  const esqueleto = (alto) => '<div style="background:' + C.panel + ';border:1px solid ' + C.borde
    + ';border-radius:10px;height:' + alto + 'px;opacity:.6"></div>';

  $el.innerHTML = '<div class="detail-section">'
    + regresoHtml()
    + '<h1 style="margin:6px 0 0;font-size:1.05rem;color:' + C.texto + '">&#128276; Cosas que necesitan tu decision</h1>'
    + '<p style="font-size:14px;color:' + C.suave + ';margin-top:4px">'
    + 'Lo que otro pidio y lo que el sistema encontro, en un solo lugar.</p>'
    + '</div>'
    + '<div data-notif-cuerpo style="margin-top:16px;display:grid;gap:14px">'
    + esqueleto(140) + esqueleto(90)
    + '</div>';

  const $cuerpo = $el.querySelector('[data-notif-cuerpo]');
  const bizId = getSelectedBusiness();

  const [aprob, ins] = await Promise.all([leerAprobaciones(bizId), leerInsumos(bizId)]);

  $cuerpo.innerHTML = bloqueAprobaciones(aprob.listado, aprob.motivo) + bloqueInsumos(ins.lista, ins.motivo);
  if (DECIDIR_DESDE_PANEL && aprob.listado && aprob.listado.porDecidir.length) conectarDecisiones($cuerpo, bizId);
}

/**
 * Cuantas SOLICITUDES esperan la decision del dueno, para la tarjeta "Aprobaciones"
 * del Dashboard (Q #25698: sumarle los insumos en cero daba "7 pendientes" a un
 * negocio sin nada que aprobar). null = no se pudo saber; 0 = se midio y no hay.
 */
export async function contarPorAprobar() {
  return (await resumenPorAprobar()).n;
}

/**
 * Lo que la tarjeta del Dashboard dice (Ares #26726): cuantas esperan y la mas vieja. n null = no se pudo saber.
 */
export async function resumenPorAprobar() {
  try {
    const { listado } = await leerAprobaciones(getSelectedBusiness());
    if (!listado) return { n: null, masVieja: null };
    return { n: listado.porDecidir.length, masVieja: laMasVieja(listado.porDecidir) };
  } catch (e) {
    return { n: null, masVieja: null };
  }
}

/**
 * Cuantas cosas hay esperando decision: solicitudes por decidir + insumos en
 * cero. Nunca lanza y nunca inventa: lo que no se pudo saber cuenta 0.
 */
export async function contarPendientesNotificaciones() {
  try {
    const bizId = getSelectedBusiness();
    if (!bizId) return 0;
    const [aprob, ins] = await Promise.all([leerAprobaciones(bizId), leerInsumos(bizId)]);
    return (aprob.listado ? aprob.listado.porDecidir.length : 0) + ins.lista.length;
  } catch (e) {
    return 0;
  }
}
